import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import type { StaffInvite } from '@manaro/contracts'
import { Button } from '@/shared/ui/button.tsx'
import { inviteMessage } from '../labels.ts'
import { StaffDialog } from './staff-dialog.tsx'

type Copied = 'link' | 'message' | null

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function InviteLinkDialog({ invite, onClose }: { invite: StaffInvite; onClose: () => void }) {
  const [copied, setCopied] = useState<Copied>(null)
  const [copyFailed, setCopyFailed] = useState(false)

  const copy = async (what: Exclude<Copied, null>) => {
    const ok = await copyText(what === 'link' ? invite.inviteUrl : inviteMessage(invite.member.name, invite.inviteUrl))
    setCopyFailed(!ok)
    setCopied(ok ? what : null)
  }

  const copyIcon = (what: Exclude<Copied, null>) =>
    copied === what ? <Check className="size-3.5" /> : <Copy className="size-3.5" />

  return (
    <StaffDialog titleId="invite-link-title" onClose={onClose}>
      {(close) => (
        <div className="flex flex-col gap-4 px-6 py-5.5">
          <div className="flex flex-col gap-1.5">
            <h2 id="invite-link-title" className="font-display text-[19px] font-semibold tracking-[-0.01em]">
              Ссылка-приглашение готова
            </h2>
            <p className="text-[14px] font-medium">
              {invite.member.name} · <span className="font-normal text-ink-muted">{invite.member.email}</span>
            </p>
            <p className="text-[14px] text-ink-muted text-balance-pretty">
              Отправьте её в Telegram или другом мессенджере: по ней сотрудник задаст пароль и войдёт. Ссылка работает
              один раз и 72 часа. Сохранить её здесь не получится — мы храним только отпечаток.
            </p>
          </div>

          <input
            readOnly
            value={invite.inviteUrl}
            aria-label="Ссылка-приглашение"
            onFocus={(event) => event.currentTarget.select()}
            className="w-full rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 font-mono text-[13px] text-ink outline-none focus:border-brand"
          />

          {copyFailed && (
            <p className="text-[13px] text-warn-ink">Браузер не дал скопировать — выделите ссылку и скопируйте вручную.</p>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" size="sm" icon={copyIcon('message')} onClick={() => void copy('message')}>
              {copied === 'message' ? 'Сообщение скопировано' : 'Скопировать с текстом'}
            </Button>
            <Button variant="secondary" size="sm" icon={copyIcon('link')} onClick={() => void copy('link')}>
              {copied === 'link' ? 'Ссылка скопирована' : 'Скопировать ссылку'}
            </Button>
            <Button size="sm" onClick={close}>
              Готово
            </Button>
          </div>
        </div>
      )}
    </StaffDialog>
  )
}
